import TimeSignature from './TimeSignature';
import type { CountInPlan } from './CountIn';
import {
  beatGridOf,
  beatGroupingOf,
  beatIndexAtOrBefore,
  beatStartInBars,
  type MeterBeatGrid,
} from './MeterTiming';

/**
 * The countdown number and the closing ring shown over the count-in of
 * ungraded play. Both are read off the count-in plan the scheduler queued on
 * the audio clock, so they follow the clicks the player hears.
 *
 * The number counts down what is left before the entry (the first note):
 *
 *   - An entry on a beat counts whole beats: 4, 3, 2, 1 in 4/4.
 *   - An entry between beats counts the subdivision it sits on, in the bar
 *     before the entry (with a one-bar count-in, the whole count-in): an entry
 *     on an "and" counts eighths (4/4, "4 and": 7 ... 1), an entry on an "e" or
 *     an "a" counts sixteenths. The clicks (and the cursor's pulse) stay on
 *     the beats; every beat is also a subdivision mark, so the numbers that
 *     fall on clicks change with them.
 *   - Speed guard: when consecutive numbers would come less than
 *     COUNTDOWN_MIN_STEP_SECS apart, the count drops one level (sixteenths to
 *     eighths, eighths to beats). The last number then lasts until the entry,
 *     which the ring still marks exactly.
 *
 * "Eighths" and "sixteenths" are the 4/4 names of the two levels below the
 * beat. In a meter whose beat is one bottom note (2/4, 3/4, 2/2) they are the
 * beat's halves and quarters. In a meter whose beats group bottom notes (6/8,
 * 9/8, 7/8) they are the bottom note and its half: 6/8's eighths and
 * sixteenths.
 *
 * Positions are grid bars, as in Models/CountIn.
 */

/**
 * The shortest time between two consecutive countdown numbers. Faster than
 * this the numbers flicker past unread, so the count drops a level. Tune it
 * on the devices.
 */
export const COUNTDOWN_MIN_STEP_SECS = 0.18;

export type CountdownLevel = 'beats' | 'eighths' | 'sixteenths';

const LEVELS_FINEST_FIRST: readonly CountdownLevel[] = ['sixteenths', 'eighths', 'beats'];

export interface CountdownMark {
  /** Where the number appears, in grid bars. */
  atBars: number;
  /** The number shown: marks left before the entry, this one included. */
  value: number;
}

export interface Countdown {
  level: CountdownLevel;
  marks: CountdownMark[];
  /** The first click: the countdown starts here. */
  firstClickBars: number;
  /** The entry: the ring closes here and the countdown ends. */
  entryBars: number;
}

export interface CountdownFrame {
  /** The number to show. */
  value: number;
  /** How much of the ring is still open, from 1 at the first click to 0 at the entry. */
  ringRemaining: number;
}

/** Positions closer than this (in bars) count as the same position. */
const SAME_POSITION_BARS = 1e-6;

const isOnGrid = (positionBars: number, unitBars: number): boolean => {
  const units = positionBars / unitBars;
  return Math.abs(units - Math.round(units)) * unitBars < SAME_POSITION_BARS;
};

/** The subdivision's length in bars; null for whole beats. */
const subdivisionBars = (level: CountdownLevel, timeSignature: TimeSignature): number | null => {
  if (level === 'beats') return null;
  const bottomNoteBars = 1 / timeSignature.numerator;
  const beatIsOneBottomNote = beatGroupingOf(timeSignature).every((group) => group === 1);
  const firstLevelBars = beatIsOneBottomNote ? bottomNoteBars / 2 : bottomNoteBars;
  return level === 'eighths' ? firstLevelBars : firstLevelBars / 2;
};

const isOnBeat = (positionBars: number, grid: MeterBeatGrid): boolean => {
  const beat = beatIndexAtOrBefore(positionBars + SAME_POSITION_BARS, 1, grid);
  return Math.abs(beatStartInBars(beat, grid) - positionBars) < SAME_POSITION_BARS;
};

/**
 * The level the entry asks for: beats on a beat, else the coarsest
 * subdivision the entry sits on. An entry on neither (a triplet, say) counts
 * beats.
 */
export const entryCountdownLevel = (
  timeSignature: TimeSignature,
  entryBars: number,
): CountdownLevel => {
  if (isOnBeat(entryBars, beatGridOf(timeSignature))) return 'beats';
  const onLevel = (['eighths', 'sixteenths'] as const).find((level) =>
    isOnGrid(entryBars, subdivisionBars(level, timeSignature) ?? 1),
  );
  return onLevel ?? 'beats';
};

/** Seconds between consecutive numbers of a subdivision level. */
const subdivisionStepSecs = (
  level: Exclude<CountdownLevel, 'beats'>,
  timeSignature: TimeSignature,
  measureSecs: number,
): number => (subdivisionBars(level, timeSignature) ?? 1) * measureSecs;

/**
 * The level shown: the entry's level, dropped while its numbers would come
 * closer than COUNTDOWN_MIN_STEP_SECS (never below whole beats).
 */
export const shownCountdownLevel = (
  timeSignature: TimeSignature,
  entryBars: number,
  measureSecs: number,
): CountdownLevel => {
  const wanted = entryCountdownLevel(timeSignature, entryBars);
  const readable = LEVELS_FINEST_FIRST.slice(LEVELS_FINEST_FIRST.indexOf(wanted)).find(
    (level) =>
      level === 'beats' ||
      subdivisionStepSecs(level, timeSignature, measureSecs) >= COUNTDOWN_MIN_STEP_SECS,
  );
  return readable ?? 'beats';
};

/** Subdivision marks from `fromBars` up to (not including) the entry. */
const subdivisionMarksBars = (fromBars: number, entryBars: number, unitBars: number): number[] => {
  const firstUnit = Math.ceil(fromBars / unitBars - SAME_POSITION_BARS / unitBars);
  const lastUnit = Math.ceil(entryBars / unitBars - SAME_POSITION_BARS / unitBars) - 1;
  return lastUnit < firstUnit
    ? []
    : Array.from(
        { length: lastUnit - firstUnit + 1 },
        (_unused, offset) => (firstUnit + offset) * unitBars,
      );
};

/**
 * The countdown for a count-in plan at a tempo (`measureSecs`, seconds per
 * bar). Subdivisions count only in the bar before the entry: from the first
 * beat at or after one bar before it. Earlier clicks (a count-in longer than a
 * bar) count as beats.
 */
export const countdownFor = (
  plan: CountInPlan,
  timeSignature: TimeSignature,
  measureSecs: number,
): Countdown => {
  const level = shownCountdownLevel(timeSignature, plan.entryBars, measureSecs);
  const unitBars = subdivisionBars(level, timeSignature);
  const clickBars = plan.clicks.map((click) => click.atBars);
  const grid = beatGridOf(timeSignature);
  const lastBarStart = plan.entryBars - 1;
  const switchBeat = isOnBeat(lastBarStart, grid)
    ? beatIndexAtOrBefore(lastBarStart + SAME_POSITION_BARS, 1, grid)
    : beatIndexAtOrBefore(lastBarStart, 1, grid) + 1;
  const switchBars = Math.max(beatStartInBars(switchBeat, grid), plan.firstClickBars);
  const positions =
    unitBars === null
      ? clickBars
      : [
          ...clickBars.filter((atBars) => atBars < switchBars - SAME_POSITION_BARS),
          ...subdivisionMarksBars(switchBars, plan.entryBars, unitBars),
        ];
  return {
    level,
    marks: positions.map((atBars, index) => ({ atBars, value: positions.length - index })),
    firstClickBars: plan.firstClickBars,
    entryBars: plan.entryBars,
  };
};

/**
 * What to draw at `nowBars`: null before the first click and from the entry
 * on (the countdown is over), else the number and the ring.
 */
export const countdownFrameAt = (countdown: Countdown, nowBars: number): CountdownFrame | null => {
  const { marks, firstClickBars, entryBars } = countdown;
  if (marks.length === 0 || nowBars < firstClickBars - SAME_POSITION_BARS || nowBars >= entryBars) {
    return null;
  }
  // Marks run in time order: the one shown is the last at or before now.
  const firstAhead = marks.findIndex((mark) => mark.atBars > nowBars + SAME_POSITION_BARS);
  const shown = marks[Math.max(0, (firstAhead === -1 ? marks.length : firstAhead) - 1)];
  const lengthBars = entryBars - firstClickBars;
  const ringRemaining =
    lengthBars > 0 ? Math.min(1, Math.max(0, (entryBars - nowBars) / lengthBars)) : 0;
  return { value: shown.value, ringRemaining };
};
